import { useT } from "../i18n/hooks.ts";
import type { TextKey } from "../i18n/es.ts";
import type { AssignmentColorField, AssignmentColorMode } from "../model/assignmentColors.ts";
import { buildColorMap, UNASSIGNED_COLOR } from "../model/assignmentColors.ts";

/** Same field→label keys used elsewhere for each of these 4 things (resultsPanel.zone, subBasinsPanel.snowZone, infoPopover.outletPoints/aquifers) — kept here instead of in assignmentColors.ts, which is plain model code with no i18n dependency. */
const FIELD_LABEL_KEY: Record<AssignmentColorField, TextKey> = {
  zoneCode: "resultsPanel.zone",
  snowZoneCode: "subBasinsPanel.snowZone",
  outletCode: "infoPopover.outletPoints",
  aquiferCode: "infoPopover.aquifers",
};

/**
 * Temporary legend for the color-by-assignment feature (SubBasinMap.tsx's
 * polygon fill / SubBasinCanvas.tsx's chip background) — shown only while
 * bulk-editing zones/snow zones/outlet points/aquifers (see each of those
 * components' own `colorMode`), unlike Legend.tsx's map legend which is
 * always available and remembers being dismissed. This one doesn't persist
 * any dismissal: it only exists while the coloring itself is active, so
 * there's nothing to remember between sessions. Rendered by both
 * components separately (not once in App.tsx) since each computes its own
 * `colorMode` independently — see SubBasinCanvas.tsx's own comment on why
 * its tabs can't just reuse EditBar's active catalog.
 */
export function AssignmentColorLegend({
  colorMode,
  besideResultsPanel,
  aboveEphemeralBanner,
}: {
  colorMode: AssignmentColorMode;
  /** True while the Results panel is ALSO open (SubBasinMap.tsx's `resultsMode`) — see workspace.css's `.assignment-color-legend--beside-results-panel` for why this needs to shift left instead of just relying on z-index. */
  besideResultsPanel?: boolean;
  /** True while ExampleBanner.tsx's bottom-centered `.ephemeral-banner` is ALSO on screen — only reachable together with `besideResultsPanel` (see workspace.css's `.assignment-color-legend--above-ephemeral-banner`): shifting left to clear the results panel moves this legend into the banner's own horizontal span, which its default bottom-right spot was otherwise clear of. */
  aboveEphemeralBanner?: boolean;
}) {
  const t = useT();
  const colorMap = buildColorMap(colorMode.entities);
  const className = [
    "assignment-color-legend",
    besideResultsPanel && "assignment-color-legend--beside-results-panel",
    aboveEphemeralBanner && "assignment-color-legend--above-ephemeral-banner",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={className}>
      <strong>{t(FIELD_LABEL_KEY[colorMode.field])}</strong>
      <ul>
        {colorMode.entities.map((e) => (
          <li key={e.code}>
            <span
              className="assignment-color-swatch"
              style={{ backgroundColor: colorMap.get(e.code) }}
            />
            {e.name}
          </li>
        ))}
        <li>
          <span className="assignment-color-swatch" style={{ backgroundColor: UNASSIGNED_COLOR }} />
          {t("assignmentLegend.unassignedLabel")}
        </li>
      </ul>
    </div>
  );
}
